import * as React from 'react';

import { useDebounceFn, useLocalStorageState } from 'ahooks';
import { Col, Form, Image, Row } from 'antd';

import {
  AntdUpload,
  AntdUploadImgCrop,
  DecimalInputNumber,
  PrimitiveRadioButton,
  PrimitiveRadioGroup,
  PrimitiveSlider,
} from 'components';
import { useWatermarkAction, useWatermarkValue } from 'context';
import type { WatermarkPosition } from 'types';
import {
  cn,
  generateBase64,
  generateDownloadFile,
  inputRule,
  localizeWatermarkPosition,
  randomId,
  STORAGE,
} from 'utils';

const {
  LOCAL: {
    WATERMARK: { PLACEHOLDER: WATERMARK_PLACEHOLDER },
  },
} = STORAGE;

type FieldType = ReturnType<typeof useWatermarkValue> & Record<string, unknown>;

const WatermarkFormContent = () => {
  const [placeholder, setPlaceholder] = useLocalStorageState(
    WATERMARK_PLACEHOLDER,
    {
      defaultValue:
        'http://placehold.jp/80/e6f7ff/40a9ff/1200x1200.png?text=Placeholder',
    },
  );
  const {
    imageId,
    minWidth,
    width,
    maxWidth,
    minHeight,
    height,
    maxHeight,
    position,
    top,
    right,
    bottom,
    left,
    opacity,
    ownerType,
  } = useWatermarkValue();
  const action = useWatermarkAction();

  const { block: positionBlock, inline: positionInline } = position ?? {};
  const [positionBlockAlign, positionBlockOffset] = positionBlock ?? [];
  const [positionInlineAlign, positionInlineOffset] = positionInline ?? [];

  const { run: debounceUpdateWidthSizes } = useDebounceFn(
    (
      debouncedMinWidth: typeof minWidth,
      debouncedWidth: typeof width,
      debouncedMaxWidth: typeof maxWidth,
    ) => {
      action({
        type: 'UPDATE_WATERMARK_WIDTH',
        payload: {
          minWidth: debouncedMinWidth,
          width: debouncedWidth,
          maxWidth: debouncedMaxWidth,
        },
      });
    },
  );
  const { run: debounceUpdateHeightSizes } = useDebounceFn(
    (
      debouncedMinHeight: typeof minHeight,
      debouncedHeight: typeof height,
      debouncedMaxHeight: typeof maxHeight,
    ) => {
      action({
        type: 'UPDATE_WATERMARK_HEIGHT',
        payload: {
          minHeight: debouncedMinHeight,
          height: debouncedHeight,
          maxHeight: debouncedMaxHeight,
        },
      });
    },
  );
  const { run: debounceUpdatePositionSizes } = useDebounceFn(
    (debouncedPosition: typeof position) => {
      const { block: debouncedPositionBlock, inline: debouncedPositionInline } =
        debouncedPosition ?? {};
      const [debouncedPositionBlockAlign, debouncedPositionBlockOffset] =
        debouncedPositionBlock ?? [];
      const [debouncedPositionInlineAlign, debouncedPositionInlineOffset] =
        debouncedPositionInline ?? [];

      action({
        type: 'UPDATE_WATERMARK_POSITION',
        payload: {
          position: debouncedPosition,
          top:
            debouncedPositionBlockAlign === 'top'
              ? debouncedPositionBlockOffset
              : undefined,
          bottom:
            debouncedPositionBlockAlign === 'bottom'
              ? debouncedPositionBlockOffset
              : undefined,
          right:
            debouncedPositionInlineAlign === 'right'
              ? debouncedPositionInlineOffset
              : undefined,
          left:
            debouncedPositionInlineAlign === 'left'
              ? debouncedPositionInlineOffset
              : undefined,
        },
      });
    },
  );
  const { run: debounceUpdateOpacity } = useDebounceFn(
    (debouncedOpacity: typeof opacity) => {
      action({
        type: 'UPDATE_WATERMARK_OPACITY',
        payload: {
          opacity: debouncedOpacity,
        },
      });
    },
  );

  if (!ownerType) return <React.Fragment />;

  const imageFileUrl = imageId
    ? generateDownloadFile(ownerType, imageId)
    : 'http://placehold.jp/d9d9d9/200x200.png';

  return (
    <Row className='p-6'>
      <Col span={24} xl={10} className='xl:pe-3'>
        <Form.Item<FieldType>
          label='پیش نمایش'
          name='placeholder'
          valuePropName='file'
        >
          <AntdUpload
            showUploadList={false}
            ownerType={ownerType}
            onChange={async ({ file: { originFileObj } }) => {
              if (originFileObj) {
                const preview = await generateBase64(originFileObj);
                setPlaceholder(preview);
              }
            }}
          >
            <Image
              preview={false}
              src={placeholder}
              alt='placeholder'
              rootClassName='size-full'
              className='h-[20.75rem]'
            />
            <Image
              preview={false}
              src={imageFileUrl}
              alt='watermark'
              rootClassName='absolute inset-0'
              className={cn('absolute p-1', !imageId && 'animate-pulse')}
              style={{
                minWidth,
                width,
                maxWidth,
                minHeight,
                height,
                maxHeight,
                top,
                bottom,
                right,
                left,
                opacity,
              }}
            />
          </AntdUpload>
        </Form.Item>
        <Form.Item<FieldType>
          name='imageId'
          valuePropName='file'
          className='mb-0'
        >
          <AntdUploadImgCrop
            ownerType={ownerType}
            fileList={
              imageId
                ? [
                    {
                      uid: imageId,
                      name: 'نهان‌نگاری',
                      status: 'done',
                      url: imageFileUrl,
                    },
                  ]
                : undefined
            }
            onChange={({ file: { status, response } }) => {
              action({
                type: 'UPDATE_WATERMARK_IMAGE',
                payload: {
                  imageId: undefined,
                },
              });

              if (status === 'done' && response) {
                const { id } = response;
                action({
                  type: 'UPDATE_WATERMARK_IMAGE',
                  payload: {
                    imageId: id,
                  },
                });
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} xl={14} className='xl:ps-3'>
        <Row>
          <Col span={24} md={8} className='md:pe-1'>
            <Form.Item<FieldType>
              label='حداقل عرض'
              name='minWidth'
              initialValue={minWidth}
            >
              <DecimalInputNumber
                placeholder='حداقل عرض نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdateWidthSizes(value, width, maxWidth);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24} md={8} className='md:px-1'>
            <Form.Item<FieldType>
              label='عرض پیش‌فرض'
              name='width'
              rules={[inputRule.required, inputRule.digit]}
              initialValue={width}
            >
              <DecimalInputNumber
                placeholder='عرض پیش‌فرض نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdateWidthSizes(minWidth, value, maxWidth);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24} md={8} className='md:ps-1'>
            <Form.Item<FieldType>
              label='حداکثر عرض'
              name='maxWidth'
              initialValue={maxWidth}
            >
              <DecimalInputNumber
                placeholder='حداکثر عرض نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdateWidthSizes(minWidth, width, value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} md={8} className='md:pe-1'>
            <Form.Item<FieldType>
              label='حداقل ارتفاع'
              name='minHeight'
              initialValue={minHeight}
            >
              <DecimalInputNumber
                placeholder='حداقل ارتفاع نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdateHeightSizes(value, height, maxHeight);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24} md={8} className='md:px-1'>
            <Form.Item<FieldType>
              label='ارتفاع پیش‌فرض'
              name='height'
              rules={[inputRule.required, inputRule.digit]}
              initialValue={height}
            >
              <DecimalInputNumber
                placeholder='ارتفاع پیش‌فرض نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdateHeightSizes(minHeight, value, maxHeight);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24} md={8} className='md:ps-1'>
            <Form.Item<FieldType>
              label='حداکثر ارتفاع'
              name='maxHeight'
              initialValue={maxHeight}
            >
              <DecimalInputNumber
                placeholder='حداکثر ارتفاع نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdateHeightSizes(minHeight, height, value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} md={8} className='md:pe-1'>
            <Form.Item<FieldType>
              label='جایگاه عمودی'
              name='positionBlockAlign'
              initialValue={positionBlockAlign}
            >
              <PrimitiveRadioGroup
                onChange={({ target: { value } }) => {
                  debounceUpdatePositionSizes({
                    ...position,
                    block: [value, positionBlockOffset],
                  });
                }}
              >
                {(['top', 'bottom'] satisfies WatermarkPosition[]).map(
                  value => {
                    const id = randomId();
                    const { label } = localizeWatermarkPosition(value);

                    return (
                      <PrimitiveRadioButton key={id} value={value}>
                        {label}
                      </PrimitiveRadioButton>
                    );
                  },
                )}
              </PrimitiveRadioGroup>
            </Form.Item>
          </Col>
          <Col span={24} md={16} className='md:ps-1'>
            <Form.Item<FieldType>
              label='فاصله عمودی'
              name='positionBlockOffset'
              rules={[inputRule.required, inputRule.digit]}
              initialValue={positionBlockOffset}
            >
              <DecimalInputNumber
                placeholder='فاصله عمودی نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdatePositionSizes({
                    ...position,
                    block: [positionBlockAlign, value],
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} md={8} className='md:pe-1'>
            <Form.Item<FieldType>
              label='جایگاه افقی'
              name='positionInlineAlign'
              initialValue={positionInlineAlign}
            >
              <PrimitiveRadioGroup
                onChange={({ target: { value } }) => {
                  debounceUpdatePositionSizes({
                    ...position,
                    inline: [value, positionInlineOffset],
                  });
                }}
              >
                {(['right', 'left'] satisfies WatermarkPosition[]).map(
                  value => {
                    const id = randomId();
                    const { label } = localizeWatermarkPosition(value);

                    return (
                      <PrimitiveRadioButton key={id} value={value}>
                        {label}
                      </PrimitiveRadioButton>
                    );
                  },
                )}
              </PrimitiveRadioGroup>
            </Form.Item>
          </Col>
          <Col span={24} md={16} className='md:ps-1'>
            <Form.Item<FieldType>
              label='فاصله افقی'
              name='positionInlineOffset'
              rules={[inputRule.required, inputRule.digit]}
              initialValue={positionInlineOffset}
            >
              <DecimalInputNumber
                placeholder='فاصله افقی نهان‌نگاری را وارد کنید'
                onChange={value => {
                  debounceUpdatePositionSizes({
                    ...position,
                    inline: [positionInlineAlign, value],
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item<FieldType>
              label='کدورت (Opacity)'
              name='opacity'
              className='mb-0 [&_.ant-form-item-label]:p-0'
              rules={[inputRule.required, inputRule.digit]}
              initialValue={opacity}
            >
              <PrimitiveSlider
                onChangeComplete={value => {
                  debounceUpdateOpacity(value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export { WatermarkFormContent };
